import React, { useContext, useEffect, useState, useRef } from 'react';
import { Box, Stack, Typography, TextField, Tooltip } from '@mui/material';
import { doubleTapProps } from '../../../../utils/functions/longPress';
import { ColumnWidthContext, ShowFormulaContext, EditableFieldRegistryContext } from '../contexts';
import { RowNum, FormulaLine } from './RowNum';
import {
  underlineSx,
  underlineWidthSx,
  getExpandedWidthFromDefault,
  getAutoColWidth,
} from '../helpers';
import {
  VALUE_COL_W,
  PCT_COL_W,
  PCT_TO_VALUE_GAP,
  VALUE_DEFAULT_CH,
  PCT_DEFAULT_CH,
  EDITABLE_BORDER,
} from '../constants';

// ความสูงกล่องค่าคงที่ — ใช้เฉพาะโหมด overlayEdit ให้กล่องค่าปกติกับกล่องแก้ไขที่ลอยทับสูงเท่ากัน
// ต้องเป็น string มีหน่วย ('15px') ไม่ใช่เลขเปล่า — sx ของ MUI ตีความเลขล้วนๆ ที่ ≤1 (ไม่ใช่ 0) เป็น
// เปอร์เซ็นต์เสมอ (height:1 กลายเป็น '100%' ไม่ใช่ 1px)
const OVERLAY_BOX_HEIGHT = '20px';

// แถวข้อมูลอ่านอย่างเดียว (ตัวเลข/ผลลัพธ์ที่คำนวณอัตโนมัติ — แก้ในนี้ไม่ได้เหมือน P5)
export function Row({ num, label, value, bold, red, green, pct, underline, formula, valueFontSize, pctSx, labelPl, textColor, labelTooltip, onValueDoubleClick, sharedWidth, hint, hintSx }) {
  const showFormula = useContext(ShowFormulaContext);
  const colWidth = useContext(ColumnWidthContext);
  const useSharedCols = sharedWidth || pct !== undefined;
  const valueWidth =
    useSharedCols && colWidth?.valueWidth
      ? colWidth.valueWidth
      : getExpandedWidthFromDefault(value, `${VALUE_COL_W}px`, VALUE_DEFAULT_CH, 28, 2);
  const pctWidth =
    useSharedCols && colWidth?.pctWidth
      ? colWidth.pctWidth
      : getExpandedWidthFromDefault(pct || '', `${PCT_COL_W}px`, PCT_DEFAULT_CH, 20, 1);

  const labelTypography = (
    <Typography
      variant="caption"
      sx={{ flex: 1, minWidth: 0, fontSize: '0.78rem', fontWeight: bold ? 700 : 400, color: textColor || '#6b7280', pl: labelPl || 0 }}
    >
      <RowNum num={num} />
      {label}
      {hint && (
        <Typography component="span" variant="caption" sx={{ fontSize: '0.65rem', color: 'text.disabled', ml: 0.5, ...hintSx }}>
          {hint}
        </Typography>
      )}
      <FormulaLine formula={formula} inline />
    </Typography>
  );

  return (
    <Box sx={{ py: 0.2 }}>
      <Stack direction="row" alignItems="center">
        {showFormula && labelTooltip ? (
          <Tooltip title={labelTooltip} arrow placement="top-start">
            {labelTypography}
          </Tooltip>
        ) : (
          labelTypography
        )}
        {pct !== undefined && (
          <Typography
            variant="caption"
            sx={{ width: pctWidth, flexShrink: 0, textAlign: 'right', fontSize: '0.72rem', color: 'text.secondary', mr: PCT_TO_VALUE_GAP, whiteSpace: 'nowrap', ...pctSx }}
          >
            {pct || ''}
          </Typography>
        )}
        <Box
          onDoubleClick={onValueDoubleClick}
          {...(onValueDoubleClick ? doubleTapProps(onValueDoubleClick) : {})}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            border: '1px solid transparent',
            borderRadius: 0.75,
            px: 0.75,
            py: 0.15,
            width: valueWidth,
            minWidth: VALUE_COL_W,
            flexShrink: 0,
            cursor: onValueDoubleClick ? 'pointer' : 'default',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: valueFontSize || '0.8rem',
              fontWeight: bold ? 700 : 400,
              color: textColor || (red ? '#dc2626' : green ? '#16a34a' : '#6b7280'),
              whiteSpace: 'nowrap',
              ...underlineSx(underline),
              ...underlineWidthSx(underline),
            }}
          >
            {value}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

// แถวข้อมูลแก้ไขได้ — คลิกค่าเพื่อเปิดช่องกรอก, blur/Enter = บันทึกทันที (เหมือนช่องกรอกมือใน P5)
// กรอบเส้นบางสีจาง (EDITABLE_BORDER) รอบตัวเลข = สัญลักษณ์บอกว่าช่องนี้แก้ไขได้ แทนไอคอนปากกา
export function EditableRow({
  num,
  label,
  value,
  displayValue,
  onSave,
  type = 'number',
  min,
  max,
  bold,
  red,
  hint,
  underline,
  pct,
  fillPct, // 0-100 — ถ้าใส่มา จะเติมพื้นหลังสีเขียวเข้มในป้ายตามสัดส่วน % (เช่น ความคืบหน้า)
  textColor, // สีตัวหนังสือกำหนดเอง — ถ้าใส่มาจะใช้แทน red/text.primary
  center, // จัดตัวเลขไว้กลางป้ายแทนชิดขวา
  valueFontSize, // ขนาดฟอนต์ของค่ากำหนดเอง — ถ้าใส่มาจะใช้แทนค่า default '0.75rem'
  labelGap, // ระยะห่าง (MUI spacing unit) ระหว่างป้ายข้อความกับกล่องค่า — ถ้าใส่มาจะดันกล่องค่าออกไปทางขวา
  borderColor: borderColorProp, // สีกรอบกล่องค่ากำหนดเอง — ถ้าใส่มาจะใช้แทน EDITABLE_BORDER เดิม
  formula, // สูตรที่มา (ถ้ามี) — โชว์ใต้แถวเฉพาะตอนเปิดสวิตช์ "แสดงสูตร"
  labelPl, // padding-left ของป้าย (MUI spacing unit) — ถ้าใส่มาจะดันป้ายเข้าไปทางขวา
  labelTooltip, // ถ้าใส่มา — โชว์เป็น tooltip ตอน hover ป้าย เฉพาะตอนเปิดสวิตช์ "แสดงสูตร"
  sharedWidth, // เข้าร่วมความกว้างร่วม (ColumnWidthContext) โดยไม่ต้องโชว์คอลัมน์ % — แยกจาก pct
  disabled, // true = คลิกแล้วไม่เปิดช่องกรอก เรียก onDisabledClick แทน
  onDisabledClick, // callback ตอนคลิกขณะ disabled — ใช้โชว์เหตุผลให้ user (เช่น snackbar)
  tabOrder, // ลำดับช่องกรอกในแผงนี้ — กด Enter แล้วเลื่อน focus ไปช่องที่ tabOrder ถัดไปให้เอง (ไม่ใส่ = ไม่เลื่อน)
  overlayEdit, // true = ตอนแก้ไข ให้ลอยทับ (position:absolute) แทนดันเลย์เอาต์ขยาย — ใช้ในกล่องแคบที่ขยายไม่ได้
}) {
  const showFormula = useContext(ShowFormulaContext);
  const colWidth = useContext(ColumnWidthContext);
  const fieldRegistry = useContext(EditableFieldRegistryContext);
  const useSharedCols = sharedWidth || pct !== undefined;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef();
  const cancelledRef = useRef(false); // true = กำลังจะ blur จากการกด ESC ยกเลิก ไม่ใช่บันทึก
  const doneRef = useRef(false); // กัน commit() ทำงานซ้ำ (เรียกตรงจาก keydown + blur ธรรมชาติจาก unmount อาจซ้อนกัน)
  const editableValueWidth =
    useSharedCols && colWidth?.valueWidth
      ? colWidth.valueWidth
      : getExpandedWidthFromDefault(displayValue, `${VALUE_COL_W}px`, VALUE_DEFAULT_CH, 28, 2);
  const editablePctWidth =
    useSharedCols && colWidth?.pctWidth
      ? colWidth.pctWidth
      : getExpandedWidthFromDefault(pct || '', `${PCT_COL_W}px`, PCT_DEFAULT_CH, 20, 1);
  const inputAutoWidth = getAutoColWidth(draft || displayValue || value || '', 10, 28);
  const inputWidth =
    type === 'date'
      ? 140
      : useSharedCols && colWidth?.valueWidth
      ? colWidth.valueWidth
      : inputAutoWidth;

  const startEdit = () => {
    // ค่า 0 ถือเป็น "ยังไม่กรอก" เหมือนกัน — เริ่มช่องแก้แบบว่างไว้ กัน user ต้องลบเลข 0 เองก่อนพิมพ์
    cancelledRef.current = false; // กันค้างจากรอบก่อนหน้า
    doneRef.current = false;
    setDraft(value ? String(value) : '');
    setEditing(true);
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select(); // เลือกข้อความทั้งหมดไว้เลย พิมพ์ทับได้ทันทีไม่ต้องลบเอง
    }, 50);
  };

  // ลงทะเบียน startEdit ของช่องนี้ไว้กับ registry กลาง (ทุก render กันโดนัส closure ค้างค่าเก่า)
  // ให้ช่องก่อนหน้าเรียกเปิดช่องนี้ต่อได้ตอนกด Enter — ถ้า disabled (เช่นงานเสร็จ 100% แก้ progress ไม่ได้)
  // ไม่ลงทะเบียน ช่องก่อนหน้าจะข้ามไปช่องถัดไปเองแทน
  useEffect(() => {
    if (!fieldRegistry || tabOrder == null || disabled) return undefined;
    fieldRegistry.register(tabOrder, startEdit);
    return () => fieldRegistry.unregister(tabOrder);
  });

  const commit = (opts = {}) => {
    if (doneRef.current) return; // กันเรียกซ้ำ (เช่น Enter เรียกตรง แล้ว blur จาก unmount เรียกซ้ำอีกที)
    doneRef.current = true;
    setEditing(false);
    if (cancelledRef.current) {
      cancelledRef.current = false; // ESC ยกเลิก — ไม่บันทึกค่าที่พิมพ์ค้างไว้
      return;
    }
    if (type === 'date') {
      onSave(draft || null);
    } else {
      const trimmed = draft.trim();
      let next = trimmed === '' ? null : Number(trimmed);
      if (next !== null && Number.isNaN(next)) return;
      if (next !== null && min != null && next < min) next = min;
      if (next !== null && max != null && next > max) next = max;
      onSave(next);
    }
    // เฉพาะตอนกด Enter (advance:true) เท่านั้น — คลิกที่อื่น/blur เฉยๆ ไม่ควรเด้งไปช่องถัดไป
    if (opts.advance && tabOrder != null) {
      fieldRegistry?.activateNext(tabOrder);
    }
  };

  const textField = (
    <TextField
      inputRef={inputRef}
      size="small"
      type={type}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => commit()}
      onKeyDown={(e) => {
        // เรียก commit() ตรงๆ ทั้งคู่ ไม่พึ่ง blur() — จังหวะของ native blur event ตอน unmount
        // ไม่แน่นอนพอที่จะเป็นจุดเดียวที่ยืนยัน/ยกเลิกค่าได้ (doneRef กัน commit() ไม่ให้ทำงานซ้ำ
        // ถ้า blur ยังยิงมาเพิ่มทีหลังจากการ unmount)
        if (e.key === 'Enter') {
          e.preventDefault();
          commit({ advance: true }); // Enter = ยืนยัน + เลื่อนไปช่องถัดไป
        }
        if (e.key === 'Escape') {
          // stopPropagation กัน ESC ทะลุขึ้นไปปิด Dialog ของ P111 ทั้งอัน (ตั้งใจแค่ยกเลิกช่องนี้)
          e.preventDefault();
          e.stopPropagation();
          cancelledRef.current = true;
          commit();
        }
      }}
      inputProps={{ min, max, style: { fontSize: '0.75rem', padding: '2px 6px', textAlign: type === 'date' ? 'left' : 'right' } }}
      sx={
        overlayEdit
          ? {
              width: inputWidth,
              minWidth: VALUE_COL_W,
              '& .MuiOutlinedInput-root': { height: OVERLAY_BOX_HEIGHT },
            }
          : { width: inputWidth, minWidth: VALUE_COL_W, ml: labelGap || 0 }
      }
    />
  );

  return (
    <Box sx={{ py: 0.2, position: overlayEdit ? 'relative' : undefined }}>
      <Stack direction="row" alignItems="center">
      <Stack direction="row" spacing={0.5} alignItems="baseline" sx={{ flex: 1, minWidth: 0, pl: labelPl || 0 }}>
        {(() => {
          const labelTypography = (
            <Typography
              variant="caption"
              sx={{ fontSize: '0.78rem', fontWeight: bold ? 700 : 400, color: textColor || '#6b7280' }}
            >
              <RowNum num={num} />
              {label}
              <FormulaLine formula={formula} inline />
            </Typography>
          );
          return showFormula && labelTooltip ? (
            <Tooltip title={labelTooltip} arrow placement="top-start">
              {labelTypography}
            </Tooltip>
          ) : (
            labelTypography
          );
        })()}
        {hint && (
          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>
            {hint}
          </Typography>
        )}
      </Stack>
      {pct !== undefined && (
        <Typography
          variant="caption"
          sx={{ width: editablePctWidth, flexShrink: 0, textAlign: 'right', fontSize: '0.72rem', color: 'text.secondary', mr: PCT_TO_VALUE_GAP, whiteSpace: 'nowrap' }}
        >
          {pct || ''}
        </Typography>
      )}
      {editing && overlayEdit && (
        // ลอยทับแบบไม่แตะเลย์เอาต์เดิมเลย — anchor ขวาบนตำแหน่งเดียวกับกล่องค่าปกติ, popup ด้วย
        // zIndex+shadow ให้ดูลอยทับข้อความข้างๆ ได้ตามต้องการ แทนที่จะดันกล่องแม่ให้ขยายกว้างขึ้น
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 20,
            height: OVERLAY_BOX_HEIGHT,
            bgcolor: 'background.paper',
            boxShadow: 3,
            borderRadius: 0.75,
          }}
        >
          {textField}
        </Box>
      )}
      {editing && !overlayEdit ? (
        textField
      ) : !editing ? (
        <Box
          onClick={disabled ? onDisabledClick : startEdit}
          sx={{
            position: 'relative',
            display: 'flex',
            justifyContent: center ? 'center' : 'flex-end',
            alignItems: 'center',
            cursor: disabled ? 'not-allowed' : 'pointer',
            border: borderColorProp ? `1px solid ${borderColorProp}` : EDITABLE_BORDER,
            borderRadius: 0.75,
            px: 0.75,
            py: 0.15,
            height: overlayEdit ? OVERLAY_BOX_HEIGHT : undefined,
            boxSizing: 'border-box',
            width: editableValueWidth,
            minWidth: VALUE_COL_W,
            flexShrink: 0,
            ml: labelGap || 0,
            overflow: 'hidden',
            opacity: disabled ? 0.6 : 1,
            '&:hover': disabled ? {} : { borderColor: '#94a3b8', bgcolor: 'action.hover' },
          }}
        >
          {fillPct != null && (
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: `${Math.max(0, Math.min(100, fillPct))}%`,
                // เขียวสว่างเดียวกับพื้นหลังป้าย "สรุป กำไรคาดการณ์ + BG คงเหลือ" ตรงๆ (#dcfce7)
                // แทนสีเข้มแบบโปร่งแสงเดิม (ดูหม่นไป ไม่สว่างเท่าป้ายจริง)
                bgcolor: '#dcfce7',
                zIndex: 0,
              }}
            />
          )}
          <Typography
            variant="caption"
            sx={{
              position: 'relative',
              zIndex: 1,
              fontSize: valueFontSize || '0.75rem',
              fontWeight: bold ? 700 : 400,
              color: textColor || (red ? '#dc2626' : '#6b7280'),
              whiteSpace: 'nowrap',
              ...underlineSx(underline),
              ...underlineWidthSx(underline),
            }}
          >
            {displayValue}
          </Typography>
        </Box>
      ) : null}
      </Stack>
    </Box>
  );
}
